'use client';

import { useQuery } from '@tanstack/react-query';
import { query, orderBy, getDocs, onSnapshot } from 'firebase/firestore';
import { APP_KEYS } from './query-keys';
import { MoneyFlowService } from '../services/money-flow';
import { useEffect, useState } from 'react';
import { settlementsCol, expensesCol, balanceDoc } from '../services/helpers/firestore-refs';

export const useFinance = {
  /**
   * 📊 Historial de Cierres de Caja
   */
  useSettlementHistory: (ownerId: string) => {
    return useQuery({
      queryKey: [...APP_KEYS.finance.owner(ownerId), 'settlements'],
      queryFn: async () => {
        const q = query(settlementsCol(ownerId), orderBy('timestamp', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      },
      enabled: !!ownerId,
    });
  },

  /**
   * 📉 Lista de Gastos/Egresos
   */
  useExpensesList: (ownerId: string) => {
    const [data, setData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      if (!ownerId) return;
      const q = query(expensesCol(ownerId), orderBy('createdAt', 'desc'));
      const unsub = onSnapshot(q, (snap) => {
        setData(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setIsLoading(false);
      });
      return () => unsub();
    }, [ownerId]);

    return { data, isLoading };
  },

  /**
   * 💰 Balance en Tiempo Real
   */
  useBalance: (ownerId: string, balanceId: string = 'main') => {
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      if (!ownerId || !balanceId) return;
      const unsub = onSnapshot(balanceDoc(ownerId, balanceId), (snap) => {
        setData(snap.exists() ? snap.data() : null);
        setIsLoading(false);
      });
      return () => unsub();
    }, [ownerId, balanceId]);

    return { data, isLoading };
  },

  /**
   * 🛒 Mutación para Egresos
   */
  useExpenseMutation: () => {
    return MoneyFlowService.useExpenseMutation();
  },
};
